define('webshopProductsCategoriesListView',[
  'module',
  'underscore',
  'app',
  'backbone',
  'settings',
  'tableView',
  'tableHeaderView',
  'webshopProductsListImageView',
  'webshopProductsListInfosView',
  'webshopProductsListSupplierView',
  'webshopProductsCategoriesListSelectView',
  'webshopProductsCategoriesListSelectHeaderView',
  'webshopProductsCategoriesListCategoryView',
  'webshopProductsCategoriesListMainCategoryView',
  'webshopProductsCategoriesListFiltersView',
  'webshopProductsDetailView',
  'entities/webshop/products',
  'entities/webshop/categories'
], function (
  Module,
  _,
  App,
  Backbone,
  Settings,
  TableView,
  HeaderView,
  ImageView,
  InfosView,
  SupplierView,
  SelectView,
  SelectHeaderView,
  CategoryView,
  MainCategoryView,
  FiltersView,
  DetailView
) {
  'use strict';

  // always shown category columns (the grid grows when a product has more)
  var MIN_CATEGORY_COLUMNS = 3;

  Module.exports = TableView.extend({
    className: 'webshop-products-categories-list-view ' + TableView.prototype.className,
    pageSize: 50,
    pageMode: 'pagination',
    filtersViewClass: FiltersView,
    selectable: true,
    multiSelect: true,

    init: function () {
      this.selectedProducts = this.options.selectedProducts || new Backbone.Model({products: []});
      this.clipboard = this.options.clipboard || new Backbone.Model({id: '', code: '', name: ''});
      this.categoryColumns = MIN_CATEGORY_COLUMNS;
      this.categoriesRequest = null;
      // setFields() ran in TableView.initialize before categoryColumns existed
      this.fields = this.setFields();
    },

    onShow: function () {
      TableView.prototype.onShow.apply(this, arguments);

      // rows are re-created on every fetch: keep the selection in sync
      this.listenTo(this.bodyView.collection, 'add remove reset', _.debounce(_.bind(function () {
        if (!this.isDestroyed)
          this.onSelected(this.getSelected());
      }, this), 50));
    },

    onDestroy: function () {
      if (this.categoriesRequest)
        this.categoriesRequest.abort();
      if (this.allCategoriesRequest)
        this.allCategoriesRequest.abort();
      TableView.prototype.onDestroy.apply(this, arguments);
    },

    /**
     * All the webshop categories, loaded once per screen for the cells
     * autocomplete: promise of [{id, code, name, status}].
     */
    getAllCategories: function () {
      if (this.allCategoriesPromise)
        return this.allCategoriesPromise;

      var defer = $.Deferred();
      var lang = Settings.get('lang');

      this.allCategoriesRequest = $.ajax({
        type: 'GET',
        url: App.request('webshop.categories.search', {}),
        success: function (items) {
          var categories = _.map(items, function (item) {
            var name = '';
            var fallback = '';
            _.each(item.texts || [], function (text) {
              if (text.type !== 'name' || !text.content)
                return;
              if (text.lang === lang)
                name = text.content;
              else if (fallback === '')
                fallback = text.content;
            });

            return {
              id: String(item.id),
              code: item.code,
              name: name || fallback || item.label || item.code,
              status: item.status
            };
          });

          defer.resolve(_.sortBy(categories, function (category) {
            return category.code.toLowerCase();
          }));
        },
        error: function () {
          defer.reject();
        }
      });

      this.allCategoriesPromise = defer.promise();
      this.allCategoriesPromise.fail(_.bind(function () {
        // allow a retry on the next cell
        this.allCategoriesPromise = null;
      }, this));

      return this.allCategoriesPromise;
    },

    setFields: function () {
      var fields = [];

      fields.push({
        name: 'Select',
        className: 'select',
        view: SelectView,
        headerView: SelectHeaderView
      });

      fields.push({
        name: 'Image',
        className: 'image',
        view: ImageView
      });

      fields.push({
        name: 'Infos',
        label: _.i18n('common.product'),
        className: 'infos',
        view: InfosView
      });

      fields.push({
        name: 'Supplier',
        label: _.i18n('product.supplier'),
        className: 'supplier',
        view: SupplierView
      });

      fields.push({
        name: 'MainCategory',
        label: _.i18n('webshop.products.categories.list.mainCategory', null, 'Main category'),
        className: 'category main-category',
        view: MainCategoryView
      });

      var columns = this.categoryColumns || MIN_CATEGORY_COLUMNS;
      for (var i = 0; i < columns; i++) {
        fields.push({
          name: 'Category' + i,
          label: _.i18n('webshop.products.categories.list.category', null, 'Category') + ' ' + (i + 1),
          className: 'category',
          index: i,
          view: CategoryView
        });
      }

      return fields;
    },

    fetch: function (first, pageSize, sortField, sortDirection, filters) {
      var defer = $.Deferred();

      var params = this.getFiltersParams(filters);
      params.first = first;
      params.pageSize = pageSize;

      if (this.fetchRequest)
        this.fetchRequest.abort();

      this.fetchRequest = $.ajax({
        type: 'GET',
        url: App.request('webshop.products.search', params),
        success: _.bind(function (products) {
          var models = [];

          _.each(products, function (item) {
            models.push(App.request('webshop.product.model', item));
          });

          // the category cells are loaded in one call for the whole page,
          // the products are shown even if that call fails
          this.loadCategories(models).always(_.bind(function () {
            if (this.updateColumns(models))
              this.bodyView.collection.reset();
            defer.resolve(models);
          }, this));
        }, this)
      });

      return defer.promise();
    },

    fetchCount: function (filters) {
      var defer = $.Deferred();

      var params = this.getFiltersParams(filters);

      if (this.fetchCountRequest)
        this.fetchCountRequest.abort();

      this.fetchCountRequest = $.ajax({
        type: 'GET',
        url: App.request('webshop.products.count', params),
        success: function (count) {
          defer.resolve(count);
        }
      });

      return defer.promise();
    },

    /**
     * GET webshop/products/categories?ids=... -> {productId: [{id, code, name}]}
     * and stores the result on each product model ("categories" attribute).
     */
    loadCategories: function (models) {
      var defer = $.Deferred();

      if (models.length === 0) {
        defer.resolve();
        return defer.promise();
      }

      var ids = _.map(models, function (model) {
        return model.getId();
      });

      this.categoriesRequest = $.ajax({
        type: 'GET',
        url: App.request('webshop.products.categories', {ids: ids.join(',')}),
        success: function (data) {
          _.each(models, function (model) {
            var categories = data ? data[model.getId()] : null;
            model.set('categories', categories ? categories : []);
          });
          defer.resolve();
        },
        error: function () {
          defer.reject();
        }
      });

      return defer.promise();
    },

    /**
     * Grows the grid so that every product of the page has one free cell.
     * Returns true when the columns changed (header and rows must be rebuilt).
     */
    updateColumns: function (models) {
      var needed = MIN_CATEGORY_COLUMNS;

      _.each(models || this.bodyView.collection.models, function (model) {
        var categories = model.get('categories') || [];
        if (categories.length + 1 > needed)
          needed = categories.length + 1;
      });

      if (needed === this.categoryColumns)
        return false;

      this.categoryColumns = needed;
      this.fields = this.setFields();

      if (this.header && !this.isDestroyed) {
        this.headerView = new HeaderView({
          tableView: this
        });
        this.getRegion('headerRegion').show(this.headerView);
      }

      return true;
    },

    /**
     * Reloads the categories of one product (after a cell action).
     */
    refreshProduct: function (model) {
      return this.loadCategories([model]).done(_.bind(function () {
        if (this.isDestroyed)
          return;

        if (this.updateColumns()) {
          var models = this.bodyView.collection.models.slice();
          this.bodyView.collection.reset();
          this.bodyView.collection.add(models);
        }
      }, this));
    },

    /**
     * Reloads the categories of the rows currently displayed (after a mass
     * update) without losing the selection when the columns do not change.
     */
    refreshCategories: function () {
      if (this.isDestroyed)
        return;

      var filters = this.filters;
      if (filters && filters.get('noCategory') === '1') {
        // the updated products may not match the filter anymore
        this.refresh();
        return;
      }

      var models = this.bodyView.collection.models.slice();
      this.loadCategories(models).done(_.bind(function () {
        if (this.isDestroyed)
          return;

        if (this.updateColumns()) {
          this.bodyView.collection.reset();
          this.bodyView.collection.add(models);
        }
      }, this));
    },

    getFiltersParams: function (filters) {
      var params = {};

      _.each(filters.attributes, function (value, key) {
        if (key !== 'dateFrom' &&
            key !== 'dateTo' &&
            key !== 'defaultCategory' &&
            key !== 'outdated' &&
            key !== 'noCategory') {
          if (value !== '')
            params[key] = value;
        }
      }, this);

      if (filters.get('dateFrom') !== '' && filters.get('dateTo') !== '') {
        params.dateFrom = filters.get('dateFrom');
        params.dateTo = filters.get('dateTo');
      }

      if (filters.get('defaultCategory') && filters.get('defaultCategory').id !== '')
        params.categoryId = filters.get('defaultCategory').id;

      if (filters.get('outdated') === true)
        params.outdated = true;

      if (filters.get('noCategory') === '1')
        params.noCategory = '1';

      return params;
    },

    onSelected: function (rows) {
      var models = [];
      _.each(rows, function (row) {
        models.push(row.model);
      });
      this.selectedProducts.set('products', models);
    },

    onRowDoubleClick: function (view) {
      DetailView.prototype.open(view.model.getId(), 'categories');
    }
  });
});

